import React, { useState, useEffect } from "react";
import { usePopup } from "../Popup/PopupContext";
import PopupModel from "../Popup/PopupModel";
import "./CheckoutPageComponents.css";

import config from "../../config";
import apiClient from "../../services/apiClient";
const BASE_API = config.API_BASE_URL;

export interface ContactInfo {
  full_name: string;
  email: string;
  phone?: string;
}

interface ContactFormProps {
  setIsValidContactInfo: (value: boolean) => void;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
  contactInfo: ContactInfo;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const RefactoredContactForm: React.FC<ContactFormProps> = ({
  setIsValidContactInfo,
  setContactInfo,
  contactInfo,
}) => {
  const [isInteracting, setIsInteracting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { showPopup } = usePopup();
  const [formComplete, setFormComplete] = useState(false);

  const debouncedFormData = useDebounce(contactInfo, 1000);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setContactInfo({
      ...contactInfo,
      [name]: value,
    });
    setIsInteracting(true);
    setIsValidContactInfo(false);
  };

  const handleContactErrorPopup = () => {
    const errorList = (
      <ul>
        {errors.map((err, index) => (
          <li key={index}>{err}</li>
        ))}
      </ul>
    );
    const contactErrorPopup = new PopupModel(
      "Contact Info - Error",
      (
        <div>
          <strong>
            Invalid contact information was provided due to the following:
          </strong>
          {errorList}
        </div>
      ),
      [],
      "40vw"
    );

    showPopup(
      contactErrorPopup.header,
      contactErrorPopup.body,
      contactErrorPopup.buttons,
      contactErrorPopup.width
    );
    setFormComplete(false);
  };

  const validateContactInfo = () => {
    const { full_name, email, phone } = contactInfo;
    const newErrors: string[] = [];

    const isFormComplete = full_name !== "" && email !== "";

    if (isFormComplete) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        newErrors.push("Please enter a valid email address");
      }

      if (phone && phone.trim() !== "") {
        const phoneRegex = /^\d{10}$|^\d{3}-\d{3}-\d{4}$/;
        if (!phoneRegex.test(phone.replace(/\s+/g, ""))) {
          newErrors.push(
            "If providing a phone number, please use format: XXX-XXX-XXXX or XXXXXXXXXX"
          );
        }
      }

      if (full_name.trim().split(/\s+/).length < 2) {
        newErrors.push("Please enter your full name (first and last name)");
      }

      // try {
      //   const response = await apiClient.post(
      //     `${BASE_API}checkout/check-contact-info/`,
      //     {
      //       formData,
      //     }
      //   );
      //   if (!response.data.is_valid) {
      //     response.data.messages.forEach((message: string) => {
      //       newErrors.push(message);
      //     });
      //   }
      // } catch (error) {
      //   newErrors.push("An error occurred while validating your information");
      // }
    }

    setErrors(newErrors);
    setIsValidContactInfo(isFormComplete && newErrors.length === 0);
  };

  useEffect(() => {
    if (isInteracting) {
      validateContactInfo();
      setIsInteracting(false);
    }
  }, [debouncedFormData]);

  useEffect(() => {
    if (errors.length > 0) {
      handleContactErrorPopup();
    } else if (formComplete) {
      setContactInfo(contactInfo);
    }
  }, [errors, formComplete]);

  return (
    <div style={{ position: "relative" }}>
      <form autoComplete="off" style={styles.formContainer}>
        <div style={styles.contactBox}>
          <div style={styles.contactHeader}>Contact Information</div>
          <div className="checkout-page-contact-form-group">
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={contactInfo.full_name}
              onChange={handleChange}
              style={styles.inputField}
              placeholder="Full Name"
              required
            />
          </div>
          <div className="checkout-page-contact-form-group">
            <input
              type="email"
              id="email"
              name="email"
              value={contactInfo.email}
              onChange={handleChange}
              style={styles.inputField}
              placeholder="Email Address"
              required
            />
          </div>
          <div className="checkout-page-contact-form-group">
            <input
              type="tel"
              id="phone"
              name="phone"
              value={contactInfo.phone}
              onChange={handleChange}
              style={styles.inputField}
              placeholder="Phone Number (Optional)"
            />
            <div style={styles.phoneNote}>
              Phone number will only be used for faster resolution of any
              order-related issues.
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RefactoredContactForm;

const styles = {
  formContainer: {
    padding: "1% 2.5%",
  },
  contactHeader: {
    fontSize: "2vm",
    marginBottom: "1vm",
    fontWeight: "700",
  },
  contactBox: {
    border: "2px solid #aaa",
    padding: "3vh",
    marginTop: "1vh",
    marginBottom: "3vh",
    borderRadius: "0",
    backgroundColor: "#ffffff",
  },
  inputField: {
    width: "100%",
    border: "2px solid #ccc",
    borderRadius: "0",
    backgroundColor: "white",
    outline: "none",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
    marginBottom: "1vh",
    padding: "1vh",
  },
  phoneNote: {
    fontSize: "0.9em",
    color: "#666",
    marginTop: "-0.5vh",
    marginBottom: "1vh",
    fontStyle: "italic",
  },
};
